<template>
  <Teleport to="body">
    <!-- Modal Backdrop -->
    <div 
      v-if="isOpen" 
      class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 flex items-center justify-center"
      @click="handleBackdropClick"
      @keydown.esc="closeModal"
      tabindex="-1"
    >
      <!-- Modal Container -->
      <div 
        class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden"
        @click.stop
        ref="modalRef"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="messageId"
      >
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex items-center">
          <div v-if="showIcon" class="mr-3">
            <!-- Different icons based on iconType -->
            <div v-if="iconType === 'warning'" class="text-yellow-500">
              <!-- Warning Icon -->
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
            </div>
            <div v-else-if="iconType === 'error'" class="text-red-500">
              <!-- Error Icon -->
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div v-else-if="iconType === 'success'" class="text-green-500">
              <!-- Success Icon -->
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <div v-else class="text-blue-500">
              <!-- Info Icon (default) -->
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>
          <h3 :id="titleId" class="text-lg font-medium text-gray-900">{{ title }}</h3>
        </div>
        
        <!-- Modal Body -->
        <div class="px-6 py-4">
          <p :id="messageId" class="text-gray-700">{{ message }}</p>
        </div>
        
        <!-- Modal Footer -->
        <div class="px-6 py-3 bg-gray-50 flex justify-end space-x-3">
          <button
            type="button"
            class="px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="cancelButtonClass || 'bg-gray-200 hover:bg-gray-300 text-gray-800 focus:ring-gray-500'"
            @click="closeModal"
            ref="cancelButtonRef"
          >
            {{ cancelButtonText }}
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="confirmButtonClass || 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'"
            @click="confirm"
          >
            {{ confirmButtonText }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, nextTick, onMounted, onUnmounted, PropType, ref, watch } from 'vue';

export default defineComponent({
  name: 'ConfirmationModal',
  
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    message: {
      type: String,
      required: true
    },
    confirmButtonText: {
      type: String,
      default: 'Confirm'
    },
    cancelButtonText: {
      type: String,
      default: 'Cancel'
    },
    confirmButtonClass: {
      type: String,
      default: ''
    },
    cancelButtonClass: {
      type: String,
      default: ''
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    iconType: {
      type: String as PropType<'warning' | 'error' | 'info' | 'success'>,
      default: 'warning'
    }
  },
  
  emits: ['confirm', 'cancel', 'update:isOpen'],
  
  setup(props, { emit }) {
    const modalRef = ref<HTMLElement | null>(null);
    const cancelButtonRef = ref<HTMLButtonElement | null>(null);
    const titleId = ref(`modal-title-${Math.random().toString(36).substr(2, 9)}`);
    const messageId = ref(`modal-message-${Math.random().toString(36).substr(2, 9)}`);
    
    // Handle ESC key press
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && props.isOpen) {
        closeModal();
      }
    };
    
    // Focus trap inside modal
    const handleTabKey = (event: KeyboardEvent) => {
      if (!props.isOpen || !modalRef.value) return;
      
      const focusableElements = modalRef.value.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      
      // If shift + tab and on first element, move to last element
      if (event.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      } 
      // If tab and on last element, move to first element
      else if (!event.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
    };
    
    // Focus first button when modal opens
    watch(() => props.isOpen, (newValue) => {
      if (newValue) {
        nextTick(() => {
          if (cancelButtonRef.value) {
            cancelButtonRef.value.focus();
          }
        });
      }
    });
    
    // Set up event listeners
    onMounted(() => {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('keydown', handleTabKey);
    });
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleTabKey);
    });
    
    // Methods
    const closeModal = () => {
      emit('update:isOpen', false);
      emit('cancel');
    };
    
    const confirm = () => {
      emit('confirm');
      emit('update:isOpen', false);
    };
    
    const handleBackdropClick = () => {
      closeModal();
    };
    
    return {
      modalRef,
      cancelButtonRef,
      titleId,
      messageId,
      closeModal,
      confirm,
      handleBackdropClick
    };
  }
});
</script>